import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
const RunDateRangeStatement = () => {
  const [searchParams] = useSearchParams();

  const accountIds = useMemo(() => {
    const ids = searchParams.get('accountIds');
    return ids ? ids.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id)) : [];
  }, [searchParams]);

  const providerIds = useMemo(() => {
    const ids = searchParams.get('providerIds');
    return ids ? ids.split(',').filter(id => id && id.trim() !== '') : [];
  }, [searchParams]);

  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const lastRunTime = searchParams.get('lastRunTime') || null;
  const selectedDate = searchParams.get('selectedDate') || null;
  const displayDate = selectedDate || date;
  const statementNote = decodeURIComponent(searchParams.get('statementNote') || '');
  const statementFooter = decodeURIComponent(searchParams.get('statementFooter') || '');
  const dontPrintEbilling = searchParams.get('dontPrintEbilling') === 'true';
  const dontPrintZeroBalance = searchParams.get('dontPrintZeroBalance') === 'true';
  const reprint = searchParams.get('reprint') === 'true';
  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previousRunDate, setPreviousRunDate] = useState(null);
  const [runDate, setRunDate] = useState(null);
  const [labInfo, setLabInfo] = useState(null);
  const [printReady, setPrintReady] = useState(false);

  // Helper functions
  const formatIfNonZero = (amount) => {
    const safeAmount = amount || 0;
    return safeAmount !== 0 ? (Math.abs(safeAmount)) : '';
  };

  const formatCredit = (amount) => {
    const safeAmount = amount || 0;
    return safeAmount !== 0 ? `-${(Math.abs(safeAmount))}` : '';
  };

  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = String(str).replace(/^0+/, '');
    return result === '' ? '0' : result;
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Invalid Date";

    let dateObj;
    if (typeof dateStr === 'string') {
      if (dateStr.includes('T')) {
        dateObj = new Date(dateStr);
      } else {
        dateObj = new Date(dateStr + 'T00:00:00');
      }
    } else if (dateStr instanceof Date) {
      dateObj = dateStr;
    } else {
      return "Invalid Date";
    }

    if (isNaN(dateObj.getTime())) return "Invalid Date";

    const day = dateObj.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const isPreviousPeriodInvoice = (invoiceDate, previousRunDate) => {
    if (!previousRunDate) return false;

    const invoiceDateObj = new Date(invoiceDate);
    const prevDateObj = new Date(previousRunDate);

    invoiceDateObj.setHours(0, 0, 0, 0);
    prevDateObj.setHours(0, 0, 0, 0);

    return invoiceDateObj <= prevDateObj;
  };

  // Fetch lab info
  useEffect(() => {
    const fetchLabInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/auth/users",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success && response.data.users.length > 0) {
          setLabInfo(response.data.users[0]);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);

  // Fetch statements
  useEffect(() => {
    const fetchStatements = async () => {
      if (accountIds.length === 0) {
        setError('No valid account IDs provided.');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const params = {
          accountIds: accountIds.join(','),
          date,
          dontPrintEbilling,
          dontPrintZeroBalance
        };

        if (providerIds.length > 0) {
          params.providerIds = providerIds.join(',');
        }

        const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/statements/run-date-range', {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success && Array.isArray(response.data.statements)) {
          const previousRunDateValue = response.data.previousRunDate;

          const processedStatements = response.data.statements.map(statement => {
            const allInvoices = statement.invoices || [];

            const invoiceLookup = {};
            allInvoices.forEach(inv => {
              if (inv.invoiceNumber) {
                invoiceLookup[inv.invoiceNumber] = inv;
              }
            });

            // 🔥 FIXED AGING CALCULATION (based on OUTSTANDING)

const agingTotals = {
  oneTo30Days: 0,
  thirtyOneTo60Days: 0,
  sixtyOneTo90Days: 0,
  ninetyPlusDays: 0,
  totalBalance: 0
};

const today = new Date(date);

allInvoices.forEach(invoice => {
  const totalPayments = (invoice.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalCredits = (invoice.credits || []).reduce((sum, c) => sum + (c.amount || 0), 0);

  const outstanding =
    (invoice.originalAmount || invoice.total || 0) -
    totalPayments -
    totalCredits;

  if (outstanding <= 0) return; // skip fully paid

  const invoiceDate = new Date(invoice.deliveryDate || invoice.invoiceDate);
  const diffTime = today - invoiceDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 30) {
    agingTotals.oneTo30Days += outstanding;
  } else if (diffDays <= 60) {
    agingTotals.thirtyOneTo60Days += outstanding;
  } else if (diffDays <= 90) {
    agingTotals.sixtyOneTo90Days += outstanding;
  } else {
    agingTotals.ninetyPlusDays += outstanding;
  }

  agingTotals.totalBalance += outstanding;
});

            const groupedByProvider = {};
            let totalPreviousBalanceAmount = 0;

            allInvoices.forEach(invoice => {
              const invoiceKey = invoice.invoiceNumber;
              const providerName = invoice.providerName || 'Unknown Provider';

              if (!groupedByProvider[providerName]) {
                groupedByProvider[providerName] = {
                  providerId: invoice.providerId,
                  providerName: providerName,
                  invoices: [],
                  allInvoices: [],
                  total: 0,
                  previousBalanceAmount: 0, // This will store the FULL previous balance (not net)
                  totalDebits: 0,
                  totalCredits: 0,
                  providerSubtotal: 0,
                  currentPeriodDebits: 0,
                  currentPeriodCredits: 0,
                  previousPeriodCredits: 0 // Track credits applied to previous balance
                };
              }

              const totalPayments = (invoice.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
              const totalCredits = (invoice.credits || []).reduce((sum, c) => sum + (c.amount || 0), 0);
              const outstanding = (invoice.originalAmount || invoice.total || 0) - totalPayments - totalCredits;

              groupedByProvider[providerName].totalDebits += (invoice.originalAmount || invoice.total || 0);

              (invoice.payments || []).forEach(p => {
                groupedByProvider[providerName].totalCredits += p.amount || 0;
              });
              (invoice.credits || []).forEach(c => {
                groupedByProvider[providerName].totalCredits += c.amount || 0;
              });

              const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
              const isPreviousInvoice = previousRunDateValue &&
                                       isPreviousPeriodInvoice(invoiceDate, previousRunDateValue);

              if (isPreviousInvoice) {
                // Add the FULL invoice amount to previous balance, not the outstanding
                groupedByProvider[providerName].previousBalanceAmount += (invoice.originalAmount || invoice.total || 0);
                totalPreviousBalanceAmount += (invoice.originalAmount || invoice.total || 0);

                // Track credits for previous period separately
                (invoice.payments || []).forEach(p => {
                  groupedByProvider[providerName].previousPeriodCredits += p.amount || 0;
                });
                (invoice.credits || []).forEach(c => {
                  groupedByProvider[providerName].previousPeriodCredits += c.amount || 0;
                });
              } else {
                groupedByProvider[providerName].currentPeriodDebits += (invoice.originalAmount || invoice.total || 0);
                (invoice.payments || []).forEach(p => {
                  groupedByProvider[providerName].currentPeriodCredits += p.amount || 0;
                });
                (invoice.credits || []).forEach(c => {
                  groupedByProvider[providerName].currentPeriodCredits += c.amount || 0;
                });
              }

              groupedByProvider[providerName].allInvoices.push({
                ...invoice,
                outstanding: outstanding,
                transactions: invoice.transactions || [],
                isPreviousPeriod: isPreviousInvoice
              });

              if (!isPreviousInvoice) {
                groupedByProvider[providerName].invoices.push({
                  ...invoice,
                  outstanding: outstanding,
                  transactions: invoice.transactions || [],
                  isPreviousPeriod: false
                });
                groupedByProvider[providerName].total += outstanding;
              }
            });

            // Sort current invoices by date (earliest first)
            Object.keys(groupedByProvider).forEach(providerName => {
              const provider = groupedByProvider[providerName];
              if (provider.invoices && provider.invoices.length > 0) {
                provider.invoices.sort((a, b) => {
                  const dA = a.deliveryDate || a.invoiceDate;
                  const dB = b.deliveryDate || b.invoiceDate;
                  const dateA = dA ? new Date(dA.includes('T') ? dA : dA + 'T00:00:00') : new Date(2100, 0, 1);
                  const dateB = dB ? new Date(dB.includes('T') ? dB : dB + 'T00:00:00') : new Date(2100, 0, 1);
                  return dateA.getTime() - dateB.getTime();
                });
              }
            });

            // Calculate provider subtotal: (Previous Balance + Current Debits) - (Previous Credits + Current Credits)
            Object.keys(groupedByProvider).forEach(providerName => {
              const provider = groupedByProvider[providerName];
              provider.providerSubtotal =
                (provider.previousBalanceAmount || 0) +
                (provider.currentPeriodDebits || 0) -
                (provider.previousPeriodCredits || 0) -
                (provider.currentPeriodCredits || 0);
            });

            const previousTransactionsByProvider = {};
            (statement.groupedPrevious || []).forEach(group => {
              let providerName = 'Unknown Provider';
              if (group.items && group.items.length > 0) {
                providerName = group.items[0].providerName || 'Unknown Provider';
              } else if (group.invoiceNumbers && group.invoiceNumbers.length > 0) {
                const inv = allInvoices.find(inv => inv.invoiceNumber === group.invoiceNumbers[0]);
                if (inv) providerName = inv.providerName || 'Unknown Provider';
              }
              if (!previousTransactionsByProvider[providerName]) {
                previousTransactionsByProvider[providerName] = [];
              }
              previousTransactionsByProvider[providerName].push(group);
            });

            const standaloneByProvider = {};
            (statement.sortedStandalone || []).forEach(group => {
              let providerName = 'Unknown Provider';
              if (group.items && group.items.length > 0) {
                providerName = group.items[0].providerName || 'Unknown Provider';
              } else if (group.invoiceNumbers && group.invoiceNumbers.length > 0) {
                const inv = allInvoices.find(inv => inv.invoiceNumber === group.invoiceNumbers[0]);
                if (inv) providerName = inv.providerName || 'Unknown Provider';
              }
              if (!standaloneByProvider[providerName]) {
                standaloneByProvider[providerName] = [];
              }
              standaloneByProvider[providerName].push(group);
            });

            // Sort previous transactions & standalone by date (earliest first)
            Object.keys(previousTransactionsByProvider).forEach(providerName => {
              if (previousTransactionsByProvider[providerName]) {
                previousTransactionsByProvider[providerName].sort((a, b) => {
                  const dateA = new Date(a.date ? (a.date.includes('T') ? a.date : a.date + 'T00:00:00') : 2100, 0, 1);
                  const dateB = new Date(b.date ? (b.date.includes('T') ? b.date : b.date + 'T00:00:00') : 2100, 0, 1);
                  return dateA.getTime() - dateB.getTime();
                });
              }
            });

            Object.keys(standaloneByProvider).forEach(providerName => {
              if (standaloneByProvider[providerName]) {
                standaloneByProvider[providerName].sort((a, b) => {
                  const dateA = new Date(a.date ? (a.date.includes('T') ? a.date : a.date + 'T00:00:00') : 2100, 0, 1);
                  const dateB = new Date(b.date ? (b.date.includes('T') ? b.date : b.date + 'T00:00:00') : 2100, 0, 1);
                  return dateA.getTime() - dateB.getTime();
                });
              }
            });

            return {
              ...statement,
              groupedByProvider,
              groupedPrevious: statement.groupedPrevious || [],
              sortedStandalone: statement.sortedStandalone || [],
              agingTotals,
              displayDate: response.data.displayDate || date,
              previousTransactionsByProvider,
              standaloneByProvider,
              totalPreviousBalanceAmount
            };
          });

          setStatements(processedStatements);
          setPreviousRunDate(previousRunDateValue);
          setRunDate(response.data.runDate || date);
          setPrintReady(true);
        } else {
          setError(response.data.message || 'No statements data available');
          setStatements([]);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        const errorMessage = err.response?.data?.message ||
                            err.response?.data?.error ||
                            err.message ||
                            "An error occurred while fetching statements.";
        setError(errorMessage);
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStatements();
  }, [accountIds, date, dontPrintEbilling, dontPrintZeroBalance, providerIds, displayDate]);

  const handlePrint = () => window.print();

  const renderNote = (note) => {
    if (!note) return null;
    return note.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading statements for {formatDisplayDate(displayDate)}...</p>
        <p className="text-muted small">Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        <h4 className="alert-heading">Error Loading Statements</h4>
        <p>{error}</p>
        <hr />
        <div className="mb-3">
          <p><strong>Search Criteria:</strong></p>
          <ul className="mb-0">
            <li>Date: {date} ({formatDisplayDate(date)})</li>
            <li>Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</li>
            <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
            <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
          </ul>
        </div>
        <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>Try Again</button>
        <button className="btn btn-outline-secondary ms-2" onClick={() => window.close()}>Close Window</button>
      </div>
    );
  }

  if (statements.length === 0) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        <h4 className="alert-heading">No Statements Found</h4>
        <p>No statements found for the selected criteria.</p>
        <div className="mt-3">
          <p><strong>Search Criteria:</strong></p>
          <ul className="mb-0">
            <li>Date: {date} ({formatDisplayDate(date)})</li>
            <li>Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</li>
            <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
            <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
          </ul>
        </div>
        <button className="btn btn-outline-warning mt-3" onClick={() => window.close()}>Close Window</button>
      </div>
    );
  }

  return (
    <div className="statement-container">
      <div className="header-controls">
        <div className="button-container">
          <button className="print-button" onClick={handlePrint} disabled={!printReady}>Print</button>
        </div>
      </div>

      {statements.map((statement, index) => {
        const displayedInvoices = new Set();
        const providerGroups = {};

        Object.entries(statement.groupedByProvider || {}).forEach(([providerName, group]) => {
          if (!providerGroups[providerName]) {
            providerGroups[providerName] = {
              previousBalanceAmount: group.previousBalanceAmount || 0, // This is now the FULL previous balance
              previousPeriodCredits: group.previousPeriodCredits || 0, // Credits against previous balance
              currentGroups: [],
              previousBalanceTransactions: [],
              totalDebits: group.totalDebits || 0,
              totalCredits: group.totalCredits || 0,
              currentPeriodDebits: group.currentPeriodDebits || 0,
              currentPeriodCredits: group.currentPeriodCredits || 0,
              providerSubtotal: group.providerSubtotal || 0
            };
          }

          const uniqueInvoices = group.invoices.filter(inv => {
            const invoiceKey = inv.invoiceNumber;
            if (displayedInvoices.has(invoiceKey)) return false;
            displayedInvoices.add(invoiceKey);
            return true;
          });

          if (uniqueInvoices.length > 0) {
            group.invoices = uniqueInvoices;
            providerGroups[providerName].currentGroups.push(group);
          }
        });

        const previousTransactionsByProvider = statement.previousTransactionsByProvider || {};
        const standaloneByProvider = statement.standaloneByProvider || {};

        // Helper to get sortable timestamp
        const getSortDate = (dateStr) => {
          if (!dateStr) return new Date(2100, 0, 1).getTime();
          const normalized = typeof dateStr === 'string' && !dateStr.includes('T')
            ? `${dateStr}T00:00:00`
            : dateStr;
          const d = new Date(normalized);
          return isNaN(d.getTime()) ? new Date(2100, 0, 1).getTime() : d.getTime();
        };

        return (
          <div
            key={`${statement.accountId}-${index}`}
            className="statement-section"
            style={{ pageBreakBefore: index > 0 ? "always" : "auto" }}
          >
            <div className="header-section">
              <div className="lab-info">
                <strong>{labInfo?.labName || "Lab Name"}</strong>
                <br />
                {labInfo?.address || "Address not available"}
                <br />
                {labInfo?.city}, {labInfo?.state} {labInfo?.zipCode}
              </div>
              <div className="header-right">
                <div className="statement-right">
                  <strong>Statement for {formatDisplayDate(statement.displayDate || runDate)}</strong>
                  <br />
                  {labInfo?.telephone || "Not available"}
                  <br />
                  {labInfo?.labEmail || "Email not available"}
                  {previousRunDate && (
                    <div className="previous-statement">
                      Previous: {formatDisplayDate(previousRunDate)}
                    </div>
                  )}
                </div>
                <div className="logo-container-right">
                  <img src="./log.png" alt="Lab Logo" className="header-logo" />
                </div>
              </div>
            </div>

            <h3 className="statement-title">
              Statement for {formatDisplayDate(statement.displayDate || runDate)}
            </h3>

            <div className="client-payment-wrapper">
              <div className="client-info">
                <strong>{statement.accountHolder}</strong>
                <br />
                {statement.address || "Address not available"}
                <br />
                {[
                  statement.city,
                  statement.state,
                  statement.zip
                ].filter(Boolean).join(", ")}
              </div>
              <div className="payment-details">
                {statementNote ? renderNote(statementNote) : (
                  statement.note ? statement.note.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  )) : (
                    <>
                      Telephone: {statement.telephone || 'Not available'}<br />
                      Email: {statement.email || 'Not available'}
                    </>
                  )
                )}
              </div>
            </div>

            <div className="table-wrapper">
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(providerGroups)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([providerName, groups]) => {
                      const providerPreviousBalance = groups.previousBalanceAmount;
                      const providerPreviousCredits = groups.previousPeriodCredits;
                      const providerPreviousTransactions = previousTransactionsByProvider[providerName] || [];
                      const providerSubtotal = groups.providerSubtotal;

                      const renderRows = [];

                      // 1. Previous Balance (always first) - Show FULL balance
                      if (providerPreviousBalance > 0) {
                        const pbDate = previousRunDate || statement.previousRunDate || date;
                        renderRows.push({
                          sortDate: getSortDate(pbDate),
                          jsx: (
                            <tr key={`prev-balance-${providerName}`}>
                              <td>{formatDisplayDate(pbDate)}</td>
                              <td><strong>Previous Balance</strong></td>
                              <td>{(providerPreviousBalance)}</td>
                              <td></td>
                            </tr>
                          )
                        });
                      }

                      // 2. Previous Credits/Payments (against previous balance)
                      // First from grouped previous transactions
                      providerPreviousTransactions.forEach((group, idx) => {
                        renderRows.push({
                          sortDate: getSortDate(group.date),
                          jsx: (
                            <tr key={`prev-group-${providerName}-${idx}`} className="sub-row">
                              <td>{formatDisplayDate(group.date)}</td>
                              <td >{group.displayDescription}</td>
                              <td></td>
                              <td>{formatCredit(group.amount)}</td>
                            </tr>
                          )
                        });
                      });

                      // 3. Current Period Invoices (each invoice + its transactions stay together as a block)
                      groups.currentGroups.forEach((group) => {
                        group.invoices.forEach((invoice) => {
                          const patientName = getPatientName(invoice);
                          const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
                          const formattedInvoiceNumber = removeLeadingZeros(invoice.invoiceNumber);
                          const originalAmount = invoice.originalAmount || invoice.total || invoice.amount || 0;

                          const invoiceJSX = (
                            <tr key={`inv-${invoice.invoiceNumber}`}>
                              <td>{formatDisplayDate(invoiceDate)}</td>
                              <td>
                                Case Invoice {formattedInvoiceNumber} / {patientName}
                              </td>
                              <td>{(originalAmount)}</td>
                              <td></td>
                            </tr>
                          );

                          const transJSX = (invoice.transactions || [])
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((trans, tIdx) => (
                              <tr key={`trans-${invoice.invoiceNumber}-${tIdx}`} className="sub-row">
                                <td>{formatDisplayDate(trans.date)}</td>
                                <td >{trans.displayDescription}</td>
                                <td></td>
                                <td>{formatCredit(trans.amount)}</td>
                              </tr>
                            ));

                          const blockJSX = (
                            <React.Fragment key={`inv-block-${invoice.invoiceNumber}`}>
                              {invoiceJSX}
                              {transJSX}
                            </React.Fragment>
                          );

                          renderRows.push({
                            sortDate: getSortDate(invoiceDate),
                            jsx: blockJSX
                          });
                        });
                      });

                      // 4. Standalone transactions
                      (standaloneByProvider[providerName] || []).forEach((group, idx) => {
                        renderRows.push({
                          sortDate: getSortDate(group.date),
                          jsx: (
                            <tr key={`standalone-${providerName}-${idx}`} className="sub-row">
                              <td>{formatDisplayDate(group.date)}</td>
                              <td >{group.displayDescription}</td>
                              <td></td>
                              <td>{formatCredit(group.amount)}</td>
                            </tr>
                          )
                        });
                      });

                      // Sort ALL rows chronologically (Previous Balance stays at top because its date is oldest)
                      renderRows.sort((a, b) => a.sortDate - b.sortDate);

                      return (
                        <React.Fragment key={providerName}>
                          <tr className="provider-row">
                            <td colSpan="4">
                              <strong>{providerName}</strong>
                            </td>
                          </tr>
                          {renderRows.map((row) => row.jsx)}
                          {Math.abs(providerSubtotal) > 0.001 && (
                            <tr className="subtotal-row">
                              <td colSpan="2"><strong>Subtotal</strong></td>
                              <td>{(providerSubtotal)}</td>
                              <td></td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="aging-summary">
              <table className="aging-table">
                <thead>
                  <tr>
                    <th>1-30 Days</th>
                    <th>31-60 Days</th>
                    <th>61-90 Days</th>
                    <th>91+ Days</th>
                    <th>New Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{(statement.agingTotals?.oneTo30Days || 0)}</td>
                    <td>{(statement.agingTotals?.thirtyOneTo60Days || 0)}</td>
                    <td>{(statement.agingTotals?.sixtyOneTo90Days || 0)}</td>
                    <td>{(statement.agingTotals?.ninetyPlusDays || 0)}</td>
                    <td>{(statement.agingTotals?.totalBalance || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="statement-footer">
              <div>{statement.accountHolder}</div>
              <div>{statementFooter || statement.footer || 'No footer available'}</div>
              <div>Page {index + 1} of {statements.length}</div>
            </div>
          </div>
        );
      })}

      <style>{`
        .statement-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 14px;
          color: #000;
          padding: 20px;
          max-width: 100%;
          margin: auto;
        }
  
        .header-controls {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 15px;
        }
  
        .logo-container-right {
          text-align: right;
          min-width: 150px;
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
        }
  
        .header-logo {
          height: 60px;
          max-width: 120px;
          object-fit: contain;
        }
       
        .button-container {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
  
        .print-button {
          background-color: #004aad;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 16px;
        }
  
        .print-button:hover {
          background-color: #003580;
        }
  
        .print-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
  
        .statement-section {
          margin-bottom: 50px;
        }
  
        .header-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          padding-bottom: 15px;
          border-bottom: 1px solid #ccc;
        }
  
        .lab-info {
          flex: 1;
        }
  
        .header-right {
          display: flex;
          align-items: flex-start;
          gap: 20px;
        }
  
        .statement-right {
          text-align: left;
        }
     
        .previous-statement {
          font-size: 12px;
          color: #666;
          margin-top: 5px;
        }
     
        .statement-title {
          text-align: right;
          margin-top: 0;
          margin-bottom: 30px;
        }
  
        .client-payment-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          margin-bottom: 20px;
          gap: 20px;
        }
       
        .client-info {
          font-size: 13px;
        }
       
        .payment-details {
          max-width: 100%;
          font-size: 13px;
          text-align: left;
        }
       
        .table-wrapper {
          overflow-x: auto;
          margin-bottom: 30px;
        }
  
        .statement-table {
          width: 100%;
          border-collapse: collapse;
        }
     
        .statement-table th,
        .statement-table td {
          border: 1px solid #000;
          padding: 4px;
        }
     
        .statement-table th {
          background-color: #004aad;
          color: white;
          font-weight: bold;
          font-size: 13px;
          text-align: left;
        }
     
        .statement-table.center th,
        .statement-table.center td {
          text-align: center;
        }
     
        .statement-table td:nth-child(2) {
          text-align: left;
        }
     
        .statement-table td:nth-child(3),
        .statement-table td:nth-child(4) {
          text-align: right;
        }
     
        .provider-row {
          font-weight: bold;
        }
     
        .provider-row td {
          padding: 6px 8px;
        }
     
        .sub-row td {
          font-size: 12px;
          border-top: none;
          font-style: normal !important;
          font-weight: normal !important;
        }
     
        .statement-table .subtotal-row td {
          text-align: right;
          font-weight: bold;
        }
  
        .aging-summary {
          margin-top: 20px;
        }
  
        .aging-table {
          width: 100%;
          border-collapse: collapse;
        }
  
        .aging-table th {
          background-color: #004aad;
          color: white;
          border: 1px solid #000;
          padding: 4px;
          text-align: center;
          font-size: 13px;
        }
  
        .aging-table td {
          text-align: right;
          border: 1px solid #000;
          padding: 4px;
        }
  
        .statement-footer {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          padding-top: 15px;
          font-size: 12px;
          color: #666;
        }
  
        @media print {
          .header-controls {
            display: none !important;
          }
    
          .statement-container {
            padding: 0 !important;
          }
    
          .statement-section {
            margin-bottom: 0 !important;
            page-break-inside: avoid;
          }
    
          .print-button {
            display: none !important;
          }
       
          .client-payment-wrapper {
            display: flex !important;
            page-break-inside: avoid;
          }
       
          .aging-table th,
          .statement-table th {
            color: #000 !important;
            background-color: #fff !important;
            border: 1px solid #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
       
          .aging-table td,
          .statement-table td {
            border: 1px solid #000 !important;
          }
       
          .provider-row {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
       
          .statement-table {
            page-break-inside: auto !important;
          }
       
          tbody {
            page-break-inside: auto !important;
          }
       
          tr, td, th {
            page-break-inside: avoid !important;
          }
       
          thead {
            display: table-header-group;
          }
        }
      `}</style>
    </div>
  );
};

export default RunDateRangeStatement;